import React from 'react';

import classNames from 'classnames';

import { COMPARISON_MODE_PRIOR } from './ComparisonSelector';
import TrendSparkline from './TrendSparkline';

import { formatMoney } from 'Helpers/formatService';

/**
 * IB-757 — Budget Versions v2 (compare polish).
 *
 * Hero control strip of four tiles matching the agreed v2 prototype:
 *
 *  1. BASELINE — the dropdown-selected comparison version's net (black dot).
 *     Muted "select a version" prompt in the default "Previous version" mode
 *     where there is no single base.
 *  2. LATEST ESTIMATE — the live Latest Estimate's net (blue dot), with its
 *     movement vs the selected base as a sub-line (only when a base is chosen).
 *  3. ACTUALS TO DATE — a clearly-marked LIVE, never-snapshotted figure (amber
 *     pill, dashed border).
 *  4. NET POSITION ACROSS VERSIONS — the trend sparkline with a legend; its
 *     baseline-ref line follows the dropdown.
 *
 * `summary` is the payload from budgets.versions-summary:
 *   { latestEstimate: { id, name, version, net }, actuals: { value, is_live }, trend[] }.
 * `comparisonReference` is the selected base version ({ id, name, version, net,
 * revenue, expenditure }) or null in "Previous version" mode.
 * `latestEstimateExpenditure` is resolved client-side for the actuals ratio.
 */

const signedMoney = (value) => `${value >= 0 ? '+' : '−'}${formatMoney(Math.abs(value))}`;

function Tile({ live, className, children }) {
    return (
        <div
            className={classNames(
                'tw-px-4 tw-py-3 tw-rounded-lg tw-border-2',
                live ? 'tw-border-dashed tw-border-charcoal-25 tw-bg-charcoal-10' : 'tw-border-grey',
                className,
            )}
        >
            {children}
        </div>
    );
}

function Caption({ dotClass, amber, children }) {
    return (
        <div
            className={classNames(
                'tw-mb-2 tw-flex tw-items-center tw-gap-x-2 tw-text-2xs tw-font-semibold tw-uppercase tw-tracking-wide',
                amber ? 'tw-text-amber-700' : 'tw-text-charcoal-50',
            )}
        >
            {dotClass && <span className={classNames('tw-h-2 tw-w-2 tw-rounded-full', dotClass)} />}
            {children}
        </div>
    );
}

function VersionName({ name, version }) {
    return (
        <div className="tw-mt-1 tw-text-xs tw-font-medium tw-text-charcoal">
            {name} {$t('(v{{version}})', { version })} · {$t('net position')}
        </div>
    );
}

export default function VersionsHero({ summary, comparisonReference, comparisonMode, latestEstimateExpenditure }) {
    if (!summary) {
        return null;
    }

    const { latestEstimate, actuals, trend } = summary;
    const baseSelected = comparisonMode !== COMPARISON_MODE_PRIOR && comparisonReference;

    // The trend's baseline-ref line follows the Compare dropdown: it is the net
    // of the selected version. In the default "Previous version" mode there is
    // no single base, so no baseline line is drawn.
    const baselineNet = baseSelected ? comparisonReference.net : null;

    // Movement of the live Latest Estimate net vs the selected base's net, shown
    // only when a base is selected. Net rising is good (green).
    let leMovement = null;
    if (baseSelected && latestEstimate) {
        const diff = latestEstimate.net - comparisonReference.net;
        leMovement = {
            diff,
            arrow: diff === 0 ? '■' : diff > 0 ? '▲' : '▼',
            colour: diff === 0 ? 'tw-text-charcoal-50' : diff > 0 ? 'tw-text-green' : 'tw-text-red',
        };
    }

    // Actuals as a % of the Latest Estimate's expenditure (guarded).
    const actualsValue = actuals ? actuals.value : 0;
    const actualsPercent =
        typeof latestEstimateExpenditure === 'number' && latestEstimateExpenditure > 0 ? Math.round((actualsValue / latestEstimateExpenditure) * 100) : null;

    return (
        <div className="tw-mb-6 tw-grid tw-grid-cols-1 tw-gap-4 sm:tw-grid-cols-2 lg:tw-grid-cols-4">
            {/* 1 — Baseline (dropdown-selected comparison version) */}
            <Tile>
                <Caption dotClass="tw-bg-charcoal">{$t('Baseline')}</Caption>
                {baseSelected ? (
                    <>
                        <div
                            className={classNames(
                                'tw-text-2.5xl tw-font-semibold tw-leading-none',
                                comparisonReference.net >= 0 ? 'tw-text-green' : 'tw-text-red',
                            )}
                        >
                            {signedMoney(comparisonReference.net)}
                        </div>
                        <VersionName name={comparisonReference.name} version={comparisonReference.version} />
                        <div className="tw-mt-1.5 tw-text-xs tw-text-charcoal-75">
                            {$t('{{revenue}} rev · {{expenditure}} exp', {
                                revenue: formatMoney(comparisonReference.revenue),
                                expenditure: formatMoney(comparisonReference.expenditure),
                            })}
                        </div>
                    </>
                ) : (
                    <div className="tw-text-sm tw-text-charcoal-50">{$t('Select a version to compare')}</div>
                )}
            </Tile>

            {/* 2 — Latest Estimate */}
            <Tile>
                <Caption dotClass="tw-bg-blue">{$t('Latest Estimate')}</Caption>
                {latestEstimate ? (
                    <>
                        <div
                            className={classNames('tw-text-2.5xl tw-font-semibold tw-leading-none', latestEstimate.net >= 0 ? 'tw-text-green' : 'tw-text-red')}
                        >
                            {signedMoney(latestEstimate.net)}
                        </div>
                        <VersionName name={latestEstimate.name} version={latestEstimate.version} />
                        {leMovement && (
                            <div className={classNames('tw-mt-1.5 tw-flex tw-items-center tw-gap-x-1 tw-text-xs tw-font-medium', leMovement.colour)}>
                                <span>{leMovement.arrow}</span>
                                <span>{formatMoney(Math.abs(leMovement.diff))}</span>
                                <span className="tw-font-normal">{$t('vs baseline')}</span>
                            </div>
                        )}
                    </>
                ) : (
                    <div className="tw-text-sm tw-text-charcoal-50">{$t('Not set')}</div>
                )}
            </Tile>

            {/* 3 — Actuals to date (live, never snapshotted) */}
            <Tile live>
                <Caption amber>
                    {$t('Actuals to date')}
                    <span className="tw-rounded tw-border tw-border-amber-400 tw-bg-amber-50 tw-px-1.5 tw-py-0.5 tw-text-3xs tw-font-semibold tw-uppercase tw-text-amber-700">
                        {$t('LIVE · not snapshotted')}
                    </span>
                </Caption>
                <div className="tw-text-2.5xl tw-font-semibold tw-leading-none tw-text-charcoal">{formatMoney(actualsValue)}</div>
                <div className="tw-mt-1 tw-text-xs tw-font-medium tw-text-charcoal">
                    {actualsPercent !== null
                        ? $t('Expenditure recognised · {{percent}}% of latest est.', { percent: actualsPercent })
                        : $t('Expenditure recognised')}
                </div>
                <div className="tw-mt-1.5 tw-text-xs tw-text-charcoal-75">{$t('Live figure — shown for reference, never frozen into a version')}</div>
            </Tile>

            {/* 4 — Net position across versions (trend) */}
            <Tile className="tw-flex tw-flex-col">
                <Caption>{$t('Net position across versions')}</Caption>
                <div className="tw-flex tw-flex-1 tw-items-end">
                    <TrendSparkline trend={trend} baselineNet={baselineNet} />
                </div>
                <div className="tw-mt-2 tw-flex tw-gap-x-4 tw-text-2xs tw-text-charcoal-75">
                    <span className="tw-inline-flex tw-items-center tw-gap-x-1.5">
                        <span className="tw-inline-block tw-h-0.5 tw-w-3 tw-rounded tw-bg-blue" />
                        {$t('Net position')}
                    </span>
                    <span className="tw-inline-flex tw-items-center tw-gap-x-1.5">
                        <span className="tw-inline-block tw-h-0 tw-w-3 tw-border-t-2 tw-border-dashed tw-border-charcoal-25" />
                        {$t('Baseline ref')}
                    </span>
                </div>
            </Tile>
        </div>
    );
}
